import React, { useEffect, useState } from 'react';
import { api } from '../services/api';
import SearchableSelect from './SearchableSelect';
import CreateTransaction from './CreateTransaction';
import TransactionsTable from './TransactionsTable';
import AccountsSidebar from './AccountsSidebar';
import useIsMobile from '../hooks/useIsMobile';
import '../styles/TransactionPage.css';
import Icon from '@mdi/react';
import { mdiPlus, mdiFilter, mdiChevronDown, mdiChevronUp } from '@mdi/js';

const TransactionPage = () => {
  const userSettings = JSON.parse(localStorage.getItem('userSettings'));
  const defaultAccountId = userSettings?.default_account_id ? parseInt(userSettings.default_account_id, 10) : null;

  const initialTabState = {
    apiParams: {
      page: 1,
      per_page: 50,
      sort: 'date',
      dir: 'desc',
      account_id: defaultAccountId,
      subject_id: null,
      category_id: null,
      is_reconciled: null,
      date_from: null,
      date_to: null,
      amount_min: null,
      amount_max: null,
      search: null
    },
    transactions: [],
    totalPages: 1
  };

  const [tabs, setTabs] = useState([{ ...initialTabState, id: 1 }]);
  const [activeTabId, setActiveTabId] = useState(1);
  const [filterOptions, setFilterOptions] = useState({
    accounts: [],
    subjects: [],
    categories: []
  });
  // Balances live at page level: one fetch serves every sidebar item at once
  // instead of being re-fetched per view on every sort/page/filter change.
  const [accountsBalance, setAccountsBalance] = useState([]);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [isMobileToolbarOpen, setIsMobileToolbarOpen] = useState(false);
  const isMobile = useIsMobile();
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [showDuplicatePanel, setShowDuplicatePanel] = useState(false);
  const [duplicateTransaction, setDuplicateTransaction] = useState(null);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [editPanelOpen, setEditPanelOpen] = useState(false);

  const activeTab = tabs.find(tab => tab.id === activeTabId);

  const fetchBalances = async () => {
    try {
      const res = await api.get('/accounts-balance');
      setAccountsBalance(res.data);
    } catch (error) {
      console.error('Error fetching account balances:', error);
    }
  };

  // Load saved tabs on component mount
  useEffect(() => {
    const fetchSavedTabs = async () => {
      try {
        const response = await api.get('/usersettings/transaction_tabs');
        if (response.data?.value) {
          const savedData = JSON.parse(response.data.value);
          const restoredTabs = savedData.tabs.map(tab => ({
            ...initialTabState,
            id: tab.id,
            apiParams: { ...initialTabState.apiParams, ...tab.apiParams }
          }));
          setTabs(restoredTabs);
          setActiveTabId(savedData.activeTabId || restoredTabs[0]?.id);
        } else {
          createDefaultTab();
        }
      } catch (error) {
        console.error('Error loading saved tabs:', error);
        createDefaultTab();
      }
    };

    const createDefaultTab = () => {
      const defaultTab = { ...initialTabState, id: Date.now() };
      setTabs([defaultTab]);
      setActiveTabId(defaultTab.id);
    };

    fetchSavedTabs();
  }, []);

  // Add this save handler function
  const handleSaveTabs = async () => {
    try {
      const tabsToSave = tabs.map(tab => ({
        id: tab.id,
        apiParams: tab.apiParams
      }));

      await api.put('/usersettings/transaction_tabs', {
        value: { tabs: tabsToSave, activeTabId }
      });

      alert('Tab state saved successfully!');
    } catch (error) {
      console.error('Error saving tabs:', error);
      alert('Failed to save tabs. Please try again.');
    }
  };

  useEffect(() => {
    const fetchStaticData = async () => {
      try {
        const [accountsRes, subjectsRes, categoriesRes] = await Promise.all([
          api.get('/accounts'),
          api.get('/subjects'),
          api.get('/categories')
        ]);

        const filteredAccounts = accountsRes.data.filter(a => a.is_open === true);
        const sortedAccounts = filteredAccounts.sort((a, b) => a.name.localeCompare(b.name));

        setFilterOptions({
          accounts: sortedAccounts,
          subjects: subjectsRes.data,
          categories: categoriesRes.data
        });
      } catch (error) {
        console.error('Error fetching static data:', error);
      }
    };

    fetchStaticData();
    fetchBalances();
  }, []);

  useEffect(() => {
    const fetchTabData = async () => {
      try {
        const params = {
          ...activeTab.apiParams,
          ...Object.fromEntries(
            Object.entries(activeTab.apiParams).filter(([_, v]) => v !== null)
          )
        };

        const transactionsRes = await api.get('/transactions', { params });

        setTabs(prev => prev.map(tab =>
          tab.id === activeTabId ? {
            ...tab,
            transactions: transactionsRes.data?.transactions || [],
            totalPages: transactionsRes.data.pages
          } : tab
        ));
      } catch (error) {
        console.error('Error fetching tab data:', error);
      }
    };

    if (activeTab) fetchTabData();
  }, [activeTab?.apiParams]);

  const createNewTab = () => {
    const newTabId = Date.now();
    setTabs(prev => [
      ...prev,
      {
        ...initialTabState,
        id: newTabId,
      }
    ]);
    setActiveTabId(newTabId);
  };

  const closeTab = (tabId, e) => {
    e.stopPropagation();
    setTabs(prev => {
      const newTabs = prev.filter(tab => tab.id !== tabId);
      if (newTabs.length === 0) {
        createNewTab();
      }
      return newTabs;
    });

    if (activeTabId === tabId) {
      setActiveTabId(tabs[0].id);
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTabId(tabId);
    setIsFilterPanelOpen(false);
  };

  const updateTabParams = (updateFn) => {
    setTabs(prev => prev.map(tab =>
      tab.id === activeTabId ? {
        ...tab,
        apiParams: updateFn(tab.apiParams)
      } : tab
    ));
  };

  const handleSort = (sortField) => {
    updateTabParams(prev => ({
      ...prev,
      sort: sortField,
      dir: prev.sort === sortField ? (prev.dir === 'asc' ? 'desc' : 'asc') : 'desc',
      page: 1
    }));
  };

  const handleFilterChange = (filterName, value) => {
    updateTabParams(prev => ({
      ...prev,
      [filterName]: value || null,
      page: 1
    }));
  };

  const handleItemsPerPageChange = (newPerPage) => {
    updateTabParams(prev => ({
      ...prev,
      per_page: newPerPage,
      page: 1
    }));
  };

  const paginate = (pageNumber) => {
    updateTabParams(prev => ({
      ...prev,
      page: pageNumber
    }));
  };

  const handleReconciledChange = async (transactionId, isReconciled) => {
    try {
      await api.patch(`/transactions/${transactionId}`, { is_reconciled: isReconciled });

      setTabs(prev => prev.map(tab =>
        tab.id === activeTabId ? {
          ...tab,
          transactions: tab.transactions.map(t =>
            t.id === transactionId ? { ...t, is_reconciled: isReconciled } : t
          )
        } : tab
      ));

      fetchBalances(); // Refresh the sidebar balances
    } catch (error) {
      console.error('Error updating transaction:', error);
    }
  };

  const handleDeleteClick = async (transaction) => {
    const confirmDelete = window.confirm(
      `Delete transaction from ${transaction.date} for ${transaction.amount}?`
    );

    if (confirmDelete) {
      try {
        await api.delete(`/transactions/${transaction.id}`);
        updateTabParams(prev => ({ ...prev })); // Refresh data
        fetchBalances(); // Refresh the sidebar balances
      } catch (error) {
        console.error('Error deleting transaction:', error);
        alert('Delete failed');
      }
    }
  };

  const handleEditClick = (transaction) => {
    setEditingTransaction(transaction);
    setEditPanelOpen(true);
  };

  const handleDuplicateClick = (transaction) => {
    setDuplicateTransaction({
      ...transaction,
      id: undefined,
      date: new Date().toISOString().split('T')[0],
    });
    setShowDuplicatePanel(true);
  };

  const handleCancelEdit = () => {
    setEditPanelOpen(false);
    setEditingTransaction(null);
  };

  return (
    <div className="card transactions-page">
      <div className="transactions-layout">
        <AccountsSidebar
          tabs={tabs}
          activeTabId={activeTabId}
          accounts={filterOptions.accounts}
          accountsBalance={accountsBalance}
          onSelect={handleTabChange}
          onClose={closeTab}
          onSaveTabs={handleSaveTabs}
          onAdd={createNewTab}
        />

        <div className="transactions-main">
          <h2 className="page-title">Transactions</h2>

          <div className="header">
            <div className="account-selector-container">
              <div className="account-selector">
                <SearchableSelect
                  options={filterOptions.accounts.map(account => ({
                    value: account.id,
                    label: account.name
                  }))}
                  onSelect={(option) => handleFilterChange('account_id', option?.value || '')}
                  placeholder="Select Account"
                  value={activeTab?.apiParams.account_id}
                />
              </div>
            </div>

            {isMobile ? (
              <>
                <button
                  className="btn btn--secondary mobile-toolbar-toggle"
                  onClick={() => setIsMobileToolbarOpen(!isMobileToolbarOpen)}
                >
                  <Icon path={isMobileToolbarOpen ? mdiChevronUp : mdiChevronDown} size={0.8} />
                  {isMobileToolbarOpen ? 'Hide Actions' : 'Show Actions'}
                </button>
                {isMobileToolbarOpen && (
                  <div className='buttons-bar mobile-buttons-bar'>
                    <button
                      className="btn btn--secondary"
                      onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
                    >
                      <Icon path={mdiFilter} size={0.8} />
                      {isFilterPanelOpen ? 'Hide Filters' : 'Show Filters'}
                    </button>
                    <button
                      className="btn btn--primary"
                      data-tour="new-transaction"
                      onClick={() => {
                        setShowCreatePanel(true);
                        setIsMobileToolbarOpen(false);
                      }}
                    >
                      <Icon path={mdiPlus} size={0.8} />
                      New Transaction
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className='buttons-bar'>
                <button
                  className="btn btn--secondary"
                  onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
                >
                  <Icon path={mdiFilter} size={0.8} />
                  {isFilterPanelOpen ? 'Hide Filters' : 'Show Filters'}
                </button>
                <button
                  className="btn btn--primary"
                  data-tour="new-transaction"
                  onClick={() => {
                    setShowCreatePanel(true);
                  }}
                >
                  <Icon path={mdiPlus} size={0.8} />
                  New Transaction
                </button>
              </div>
            )}
          </div>

          <div className="transaction-table">
            {isFilterPanelOpen && (
              <div className="filter-panel">
                <div className="filter-group">
                  <SearchableSelect
                    options={filterOptions.accounts.map(account => ({
                      value: account.id,
                      label: account.name
                    }))}
                    onSelect={(option) => handleFilterChange('account_id', option?.value || '')}
                    placeholder="Filter Account"
                    value={activeTab?.apiParams.account_id}
                  />
                </div>

                <div className="filter-group">
                  <SearchableSelect
                    options={filterOptions.subjects.map(subject => ({
                      value: subject.id,
                      label: subject.name
                    }))}
                    onSelect={(option) => handleFilterChange('subject_id', option?.value || '')}
                    placeholder="Filter Subject"
                    value={activeTab?.apiParams.subject_id}
                  />
                </div>

                <div className="filter-group">
                  <SearchableSelect
                    options={filterOptions.categories.map(category => ({
                      value: category.id,
                      label: category.full_name
                    }))}
                    onSelect={(option) => handleFilterChange('category_id', option?.value || '')}
                    placeholder="Filter Category"
                    value={activeTab?.apiParams.category_id}
                  />
                </div>

                <div className="filter-group">
                  <SearchableSelect
                    options={[
                      { value: '', label: 'All' },
                      { value: 'true', label: 'Yes' },
                      { value: 'false', label: 'No' }
                    ]}
                    onSelect={(option) => handleFilterChange('is_reconciled', option?.value || '')}
                    placeholder="Filter Reconciled"
                    value={activeTab?.apiParams.is_reconciled || ''}
                  />
                </div>

                <div className="filter-group-range">
                  <input
                    type="date"
                    placeholder="Start Date"
                    value={activeTab?.apiParams.date_from || ''}
                    onChange={(e) => handleFilterChange('date_from', e.target.value || null)}
                  />
                  <span style={{ alignSelf: 'center' }}>-</span>
                  <input
                    type="date"
                    placeholder="End Date"
                    value={activeTab?.apiParams.date_to || ''}
                    onChange={(e) => handleFilterChange('date_to', e.target.value || null)}
                  />
                </div>

                <div className="filter-group-range">
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Min"
                    value={activeTab?.apiParams.amount_min || ''}
                    onChange={(e) => handleFilterChange('amount_min', e.target.value ? parseFloat(e.target.value) : null)}
                    style={{ width: '80px' }}
                  />
                  <span style={{ alignSelf: 'center' }}>-</span>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Max"
                    value={activeTab?.apiParams.amount_max || ''}
                    onChange={(e) => handleFilterChange('amount_max', e.target.value ? parseFloat(e.target.value) : null)}
                    style={{ width: '80px' }}
                  />
                </div>
              </div>
            )}

            {activeTab && (
              <TransactionsTable
                transactions={activeTab.transactions}
                totalPages={activeTab.totalPages}
                apiParams={activeTab.apiParams}
                onSort={handleSort}
                onPaginate={paginate}
                onItemsPerPageChange={handleItemsPerPageChange}
                onReconciledChange={handleReconciledChange}
                onEdit={handleEditClick}
                onDuplicate={handleDuplicateClick}
                onDelete={handleDeleteClick}
              />
            )}

            {showCreatePanel && (
              <div className="edit-panel create-panel">
                <div className="panel-header">
                  <h3>New Transaction</h3>
                  <button onClick={() => setShowCreatePanel(false)}>×</button>
                </div>
                <CreateTransaction
                  accountId={activeTab?.apiParams.account_id}
                  onCancel={() => {
                    setShowCreatePanel(false);
                  }}
                  onSuccess={() => {
                    setShowCreatePanel(false);
                    updateTabParams(prev => ({ ...prev })); // Refresh current view
                    fetchBalances();
                  }}
                />
              </div>
            )}

            {editPanelOpen && (
              <div className="edit-panel">
                <div className="panel-header">
                  <h3>Edit Transaction</h3>
                  <button onClick={handleCancelEdit}>×</button>
                </div>
                <CreateTransaction
                  editMode={true}
                  existingTransaction={editingTransaction}
                  onCancel={handleCancelEdit}
                  onSuccess={() => {
                    handleCancelEdit();
                    updateTabParams(prev => ({ ...prev })); // Refresh current view
                    fetchBalances();
                  }}
                />
              </div>
            )}

            {showDuplicatePanel && (
              <div className="edit-panel create-panel">
                <div className="panel-header">
                  <h3>Duplicate Transaction</h3>
                  <button onClick={() => setShowDuplicatePanel(false)}>×</button>
                </div>
                <CreateTransaction
                  accountId={activeTab?.apiParams.account_id}
                  existingTransaction={duplicateTransaction}
                  onCancel={() => setShowDuplicatePanel(false)}
                  onSuccess={() => {
                    setShowDuplicatePanel(false);
                    updateTabParams(prev => ({ ...prev })); // Refresh current tab
                    fetchBalances();
                  }}
                />
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default TransactionPage;